import { FC, useCallback, useMemo, useState } from "react";

import { Checkbox } from "@/components/_atoms/checkbox";
import { IconCircleSolid } from "@/components/_icons/IconCircleSolid";
import { MenuDotsButton } from "@/components/_molecules/menu-dots-button";
import { InstanceCountTag } from "@/domain/_shared/components/instance-count-tag";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useCheckPermission } from "@/domain/_shared/permission/hooks/api/use-check-permission";
import { useGetLabel } from "@/domain/label/hooks/api/use-get-label";
import { cn } from "@/lib/cn";

import { LabelActionButton } from "./label-action-button/label-action-button";
import { type LabelAssignmentContext } from "./label-assignment-context";
import { LabelEditForm } from "./label-edit-form";

interface LabelListItemProps {
  labelId: string;
  workspaceId: string;
  className?: string;
  labelAssignmentContext?: LabelAssignmentContext;
  allowMultiSelect?: boolean;
}

export const LabelListItem: FC<LabelListItemProps> = ({
  labelId,
  workspaceId,
  className,
  labelAssignmentContext,
  allowMultiSelect = true,
}) => {
  const [editMode, setEditMode] = useState(false);
  const { t } = useDomainTranslation("common");

  const { hasPermission: hasPermissionToManageWorkspace } = useCheckPermission({
    resource: "workspace",
    resourceId: workspaceId,
    action: "manage",
  });
  const { label: labelData, loading } = useGetLabel({ id: labelId });

  const labelInstance = useMemo(() => {
    if (!labelAssignmentContext) return undefined;
    return labelAssignmentContext.labelInstanceList.find((labelInstanceItem) => labelInstanceItem.labelId === labelId);
  }, [labelAssignmentContext, labelId]);

  const isAssigned = !!labelInstance;

  const handleToggleAssignment = useCallback(() => {
    if (!labelAssignmentContext) return;

    if (isAssigned && labelInstance) {
      labelAssignmentContext.deleteLabelInstance({ id: labelInstance.id });
    } else {
      labelAssignmentContext.createLabelInstance({
        labelId,
        ownerId: labelAssignmentContext.ownerId,
        ownerType: labelAssignmentContext.ownerType,
        workspaceId: labelAssignmentContext.workspaceId,
      });
    }
  }, [labelAssignmentContext, isAssigned, labelInstance, labelId]);

  if (loading || !labelData) {
    return <></>;
  }

  return (
    <div className={cn(["flex w-full flex-row items-center gap-2 rounded-md px-2 py-1 hover:bg-muted/60", className])}>
      {!editMode && (
        <div className="flex w-full flex-row items-center justify-between">
          {labelAssignmentContext ? (
            <div className="flex w-7 items-center justify-center">
              {allowMultiSelect ? (
                <Checkbox
                  checked={isAssigned}
                  onCheckedChange={handleToggleAssignment}
                  aria-label={t("label.assignLabelAriaLabel", { title: labelData.title })}
                />
              ) : (
                <button
                  type="button"
                  role="radio"
                  aria-checked={isAssigned}
                  onClick={handleToggleAssignment}
                  aria-label={t("label.assignLabelAriaLabel", { title: labelData.title })}
                  className={cn(
                    "h-4 w-4 rounded-full border border-border shadow-sm",
                    isAssigned && "border-primary bg-primary",
                  )}
                >
                  {isAssigned && (
                    <span className="flex items-center justify-center">
                      <span className="h-1.5 w-1.5 rounded-full bg-white" />
                    </span>
                  )}
                </button>
              )}
            </div>
          ) : (
            <div className="w-7"></div>
          )}
          <div className="mr-1 w-6">
            <IconCircleSolid className="mx-auto h-2.5 w-2.5" style={{ color: labelData.color }} />
          </div>
          <div className="flex grow items-center gap-1">
            <span>{labelData.title}</span>
            <InstanceCountTag instanceList={labelData.instanceList} className="ml-1 inline text-muted-foreground" />
          </div>
          <div>
            {hasPermissionToManageWorkspace && (
              <LabelActionButton
                label={{ id: labelId }}
                workspace={{ id: workspaceId }}
                handleEditLabel={() => setEditMode(true)}
                asChild
              >
                <MenuDotsButton aria-label={t("label.openActionsAriaLabel", { title: labelData.title })} />
              </LabelActionButton>
            )}
          </div>
        </div>
      )}
      {editMode && (
        <LabelEditForm
          label={labelData}
          onCanceled={() => {
            setEditMode(false);
          }}
          onSuccess={() => {
            setEditMode(false);
          }}
        />
      )}
    </div>
  );
};
